/** @jsx jsx */
import { useState } from "react"
import { jsx, css } from "@emotion/core"
import styled from "@emotion/styled"
import { useQueryString } from "@capitaria/capitaria-utms"

import CustomAnchor from "../ui/custom-anchor"
import CheckIcon from "../ui/images/check-icon"

import { trackEvent } from "../../helpers/tracker"

const AccountTypeWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  background-color: #ffffff;
  box-shadow: 0px 15px 30px rgba(0, 0, 0, 0.15);
  border-radius: 5px;
  height: 100%;
  width: 272px;
  margin: 0 0 2rem;
  padding-bottom: 0;
  @media (min-width: 1024px) {
    margin: 0 1rem 0 0;
    padding-bottom: 1.5rem;
    width: 230px;
  }
  @media (min-width: 736px) and (max-width: 1024px) {
    padding-bottom: 1.5rem;
    width: 238px;
  }
`

const AccountTypeHeader = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  min-height: 274px;
`

const AccountTypeName = styled.h2`
  font-family: Nunito;
  font-style: normal;
  font-weight: bold;
  font-size: 30px;
  line-height: 118.1%;
  color: #1f2425;
  text-align: center;
`

const BeginningPrice = styled.p`
  font-family: "Open Sans";
  font-size: 12px;
  line-height: 16px;
  border: 1px solid #e0e0e0;
  box-sizing: border-box;
  border-radius: 5px;
  text-align: center;
  padding: 1rem 0.5rem;
  min-width: 194px;
  margin: 0;
`

const AccountTypeBody = styled.div`
  display: none;
  width: 100%;
  @media (min-width: 736px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`

const WatchMore = styled.div`
  font-family: "Open Sans";
  font-weight: bold;
  font-size: 16px;
  line-height: 22px;
  color: #363f41;
  display: none;
  padding: 1.5rem 2rem;
  @media (max-width: 736px) {
    display: block;
  }
`

const GrayBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 43px;
  width: 100%;
  background-color: #ededed;
`

const SectionLabel = styled.div`
  display: none;
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 600;
  font-size: 14.5823px;
  line-height: 20px;
  text-align: center;
  color: #363f41;
  @media (max-width: 1024px) {
    display: block;
  }
`

const CostLabel = styled.div`
  display: none;
  font-family: "Open Sans";
  font-size: 14px;
  line-height: 19px;
  text-align: center;
  color: #363f41;
  width: 100%;
  min-height: 56px;
  border-bottom: 1px solid #e0e0e0;
  @media (max-width: 1024px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`

const CostValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Open Sans";
  font-weight: bold;
  font-size: 1em;
  line-height: 22px;
  color: #363f41;
  text-align: center;
  border-bottom: 1px solid #e0e0e0;
  padding: 0.5rem 0;
  min-height: 63px;
  width: 100%;
`

const BenefitLabel = styled.div`
  display: none;
  font-family: "Open Sans";
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  color: #363f41;
  width: 100%;
  min-height: 81px;
  border-bottom: 1px solid #e0e0e0;
  @media (max-width: 1024px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`

const BenefitValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Open Sans";
  font-size: 1em;
  line-height: 22px;
  color: #363f41;
  text-align: center;
  border-bottom: 1px solid #e0e0e0;
  padding: 0.5rem 0;
  min-height: 81px;
  width: 100%;
`

const OperationLabel = styled.div`
  display: none;
  font-family: "Open Sans";
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  color: #363f41;
  width: 100%;
  min-height: 81px;
  border-bottom: 1px solid #e0e0e0;
  @media (max-width: 812px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`

const OperationValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Open Sans";
  font-size: 1em;
  line-height: 22px;
  color: #363f41;
  text-align: center;
  border-bottom: 1px solid #e0e0e0;
  padding: 0.5rem 0;
  min-height: 63px;
  width: 100%;
`

const OperationLink = styled.a`
  font-family: "Open Sans";
  font-size: 12px;
  line-height: 16px;
  color: #000000;
`

const RecommenedMessage = styled.div`
  padding: 16px 16px 14px;
  font-family: "Open Sans";
  font-weight: 600;
  font-size: 0.75em;
  text-align: center;
  background-color: #30ab76;
  color: #ffffff;
  width: 100%;
`

const recommendedBorder = {
  border: "1px solid #30ab76",
  borderTopLeftRadius: "5px",
  borderTopRightRadius: "5px",
}

const showAccountTypeBody = css`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const buttonCSS = {
  fontFamily: "Open Sans",
  fontWeight: 600,
  fontSize: "10px",
  lineHeight: "14px",
  margin: "1rem 0",
  padding: "0 6px",
  height: "46px",
  minWidth: "156px",
  "@media (max-width: 1024px)": {
    fontSize: "12px",
    lineHeight: "16px",
    minWidth: "203px",
    margin: "1rem 0 0",
  },
}

const bottomButtonCSS = {
  ...buttonCSS,
  "@media (max-width: 1024px)": {
    fontSize: "12px",
    lineHeight: "16px",
    minWidth: "203px",
    margin: "1rem 0",
  },
}

type Props = {
  recommended: boolean
  accountTypeName: string
  beginningPrice: string
  openingCost: string
  depositCost: string
  maintenanceCost: string
  newsLetter: boolean
  webinars: boolean
  instruments: string
  leverage: string
  tradingCourse: boolean
  supportTeam: boolean
  exclusiveDiscount: boolean
  specialEvents: boolean
  minVolumen: string
  maxVolumen: string
}

const AccountType = ({
  recommended,
  accountTypeName,
  beginningPrice,
  openingCost,
  depositCost,
  maintenanceCost,
  newsLetter,
  webinars,
  instruments,
  leverage,
  tradingCourse,
  supportTeam,
  exclusiveDiscount,
  specialEvents,
  minVolumen,
  maxVolumen,
}: Props) => {
  const [showMore, setShowMore] = useState(false)
  const {
    queryString,
    parsedQueryString: { mc_origen: mcOrigen },
  } = useQueryString()

  const showMoreHandler = () => {
    setShowMore(!showMore)
  }

  const onClickOpenAccount = () => {
    const eventName = `tipos-de-cuentas:create-account:${accountTypeName.toLocaleLowerCase()}`

    trackEvent(eventName, {
      mc_origen: mcOrigen,
    })
  }

  const onClickOperationalAspects = () => {
    const eventName = `tipos-de-cuentas:operational-aspects`

    trackEvent(eventName, {
      mc_origen: mcOrigen,
    })
  }

  return (
    <AccountTypeWrapper>
      <AccountTypeHeader style={recommended ? recommendedBorder : undefined}>
        <RecommenedMessage
          style={{ visibility: recommended ? "visible" : "hidden" }}
        >
          Recomendado
        </RecommenedMessage>
        <AccountTypeName>{accountTypeName}</AccountTypeName>
        <BeginningPrice>
          Capital inicial desde
          <br />
          <strong>{beginningPrice}</strong>
        </BeginningPrice>
        <CustomAnchor
          href={`https://onboarding.capitaria.com/sign-up${queryString}`}
          text={`ABRIR CUENTA ${accountTypeName}`}
          styles={buttonCSS}
          target="_blank"
          primary
          onClick={onClickOpenAccount}
        />

        <WatchMore onClick={showMoreHandler}>ver mas</WatchMore>
      </AccountTypeHeader>

      <AccountTypeBody css={showMore ? showAccountTypeBody : null}>
        <GrayBlock>
          <SectionLabel>Costos</SectionLabel>
        </GrayBlock>

        <CostLabel>Apertura cuenta</CostLabel>
        <CostValue>{openingCost}</CostValue>
        <CostLabel>Comisión depósito/retiro</CostLabel>
        <CostValue>{depositCost}</CostValue>
        <CostLabel>Mantención</CostLabel>
        <CostValue>{maintenanceCost}</CostValue>

        <GrayBlock>
          <SectionLabel>Beneficios</SectionLabel>
        </GrayBlock>

        <BenefitLabel>
          <strong>Newsletter</strong>
          <span>
            Columna de opinión + Plan de Trading semanal + Oportunidades de
            trading diarias
          </span>
        </BenefitLabel>
        <BenefitValue>{newsLetter ? <CheckIcon /> : "-"}</BenefitValue>
        <BenefitLabel>
          Acceso exclusivo a todos nuestros <strong>webinars y cursos</strong>
        </BenefitLabel>
        <BenefitValue>{webinars ? <CheckIcon /> : "-"}</BenefitValue>
        <BenefitLabel>
          <span>
            <strong>Instrumentos</strong> para operar
          </span>
        </BenefitLabel>
        <BenefitValue>{instruments}</BenefitValue>
        <BenefitLabel>
          <strong>Apalancamiento</strong>
        </BenefitLabel>
        <BenefitValue>{leverage}</BenefitValue>
        <BenefitLabel>
          <strong>Curso de Análisis Técnico</strong>
        </BenefitLabel>
        <BenefitValue>{tradingCourse ? <CheckIcon /> : "-"}</BenefitValue>
        <BenefitLabel>
          <span>
            Atención <strong>equipo de Traders</strong>
          </span>
        </BenefitLabel>
        <BenefitValue>{supportTeam ? <CheckIcon /> : "-"}</BenefitValue>
        <BenefitLabel>
          <span>
            <strong>Descuento</strong> en spread
          </span>
        </BenefitLabel>
        <BenefitValue>{exclusiveDiscount ? <CheckIcon /> : "-"}</BenefitValue>
        <BenefitLabel>
          <span>
            <strong>Invitación</strong> a evento exclusivo
          </span>
        </BenefitLabel>
        <BenefitValue>{specialEvents ? <CheckIcon /> : "-"}</BenefitValue>

        <GrayBlock>
          <SectionLabel>Montos operación</SectionLabel>
        </GrayBlock>

        <OperationLabel>
          <span>
            <strong>Lotes mínimos</strong> <br />
            <OperationLink
              href={`https://www.capitaria.com/aspectos-operativos/${queryString}`}
              target="_blank"
              onClick={onClickOperationalAspects}
            >
              *ver Aspectos Operativos
            </OperationLink>
          </span>
        </OperationLabel>
        <OperationValue>{minVolumen}</OperationValue>
        <OperationLabel>
          <span>
            <strong>Lotes máximos</strong> <br />
            <OperationLink
              href={`https://www.capitaria.com/aspectos-operativos/${queryString}`}
              target="_blank"
              onClick={onClickOperationalAspects}
            >
              *ver Aspectos Operativos
            </OperationLink>
          </span>
        </OperationLabel>
        <OperationValue>{maxVolumen}</OperationValue>

        <CustomAnchor
          href={`https://onboarding.capitaria.com/sign-up${queryString}`}
          text={`ABRIR CUENTA ${accountTypeName}`}
          styles={bottomButtonCSS}
          target="_blank"
          primary
          onClick={onClickOpenAccount}
        />
      </AccountTypeBody>
    </AccountTypeWrapper>
  )
}

export default AccountType
